import CTASection from "../components/sections/common/CTASection";
import FAQAccordion from "../components/sections/common/FAQAccordion";
import PageHeroSplit from "../components/sections/common/PageHeroSplit";
import ProcessSection from "../components/sections/common/ProcessSection";
import PageMeta from "../components/ui/PageMeta";
import LogoPlaceholder from "../components/ui/LogoPlaceholder";
import Section from "../components/ui/Section";
import SectionHeading from "../components/ui/SectionHeading";
import { ctaPhone, pageCtaContent } from "../data/ctaContent";
import { faqPreviewForAdmissions } from "../data/faqs";
import { admissionsPagesContent } from "../data/innerPages";
import { servicesProcess } from "../data/services";
import {
  getCollegeDisplayName,
  getCollegeLogoByName,
  getCollegeStateByName
} from "../utils/collegeHelpers";

const content = admissionsPagesContent.bachelorsIndia;
const BACHELORS_HERO_IMAGE =
  "https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&w=1400&q=80";

function BachelorsIndia() {
  return (
    <>
      <PageMeta title={content.meta.title} description={content.meta.description} />

      <PageHeroSplit
        badge={content.hero.badge}
        title={content.hero.title}
        description={content.hero.description}
        highlights={content.hero.highlights}
        primaryCta={{ label: "Book Counselling", to: "/contact" }}
        secondaryCta={{ label: "Explore Pathways", to: "/colleges" }}
        sideTitle="Who this is for"
        sideDescription="Ideal for families seeking practical undergraduate admission direction."
        sideItems={content.forWho}
        heroImage={BACHELORS_HERO_IMAGE}
      />

      <Section className="bg-white">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <article className="rounded-2xl border border-brand-line bg-white p-6 shadow-card sm:p-7">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-gold-strong">Overview</p>
            <p className="mt-3 text-base leading-relaxed text-brand-charcoal/84">{content.intro}</p>
          </article>

          <article className="rounded-2xl border border-brand-line bg-brand-sky/35 p-6 shadow-card sm:p-7">
            <p className="text-sm font-semibold text-brand-navy">Common student decision points</p>
            <div className="mt-4 grid gap-3">
              {content.decisionPoints.map((point) => (
                <div key={point} className="rounded-xl border border-brand-line bg-white px-3 py-2 text-sm text-brand-charcoal/84">
                  {point}
                </div>
              ))}
            </div>
          </article>
        </div>
      </Section>

      <Section>
        <SectionHeading
          badge="How We Support"
          title="Practical counselling support at every important stage"
          description="Support is designed to keep students and parents aligned from shortlisting to process completion."
        />

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {content.supportBlocks.map((block, index) => (
            <article key={block.title} className="rounded-2xl border border-brand-line bg-white p-6 shadow-card">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-navy text-sm font-bold text-white">
                {index + 1}
              </span>
              <h3 className="mt-4 text-xl font-semibold text-brand-navy">{block.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-brand-charcoal/82">{block.description}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section className="bg-white">
        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <SectionHeading
            badge="College Direction Preview"
            title="Sample institutions students often evaluate"
            description="A curated preview to support counselling discussions. Full college exploration expands in the dedicated colleges section."
          />

          <div className="grid gap-3 sm:grid-cols-2">
            {content.collegePreview.map((college) => {
              const collegeName = getCollegeDisplayName(college);
              const logoUrl = getCollegeLogoByName(college);
              const collegeState = getCollegeStateByName(college);

              return (
                <article key={college} className="rounded-xl border border-brand-line bg-white px-4 py-3 shadow-card">
                  <div className="flex items-center gap-3">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={`${collegeName} logo`}
                        className="h-10 w-10 rounded-lg border border-brand-line bg-white object-contain"
                      />
                    ) : (
                      <LogoPlaceholder
                        name={collegeName}
                        className="h-10 w-10 rounded-lg text-[11px]"
                      />
                    )}
                    <div>
                      <p className="text-sm font-semibold text-brand-navy">{collegeName}</p>
                      <p className="text-xs text-brand-charcoal/72">{collegeState}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </Section>

      <ProcessSection
        badge="Guided Process"
        title="How TheCareerSpace supports bachelor's admissions"
        description="A structured process for students and families after Class 12."
        steps={servicesProcess}
      />

      <Section className="bg-white">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <SectionHeading
            badge="Quick Clarifications"
            title="Questions families usually ask before beginning"
            description="A short FAQ preview specific to admission guidance journeys."
          />
          <FAQAccordion items={faqPreviewForAdmissions} />
        </div>
      </Section>

      <CTASection
        {...pageCtaContent.bachelorsIndia}
        phone={ctaPhone}
        secondaryCtaClassName="border-brand-gold/55 bg-brand-gold-soft text-brand-navy hover:border-brand-gold hover:bg-brand-gold hover:text-brand-navy"
      />
    </>
  );
}

export default BachelorsIndia;
